/**
 * CS-style circular radar: player-centered, map rotates with facing.
 * Static zone geometry redrawn cheaply each frame into a clipped disc.
 */
import { H, U, W, q, At, G } from "../world/zones.js";
import { V } from "./dom.js";

/** Pixels per world-metre (at internal canvas resolution / DPR). */
const SCALE = 2.35;
const DPR = 2;
/** Desktop / mobile CSS sizes (square). */
const SIZE_DESKTOP = 128;
const SIZE_MOBILE = 72;

function isMobile() {
  return (
    window.matchMedia(`(max-width: 640px), (pointer: coarse)`).matches ||
    Math.min(window.innerWidth, window.innerHeight) < 700
  );
}

/**
 * @returns {{ el: HTMLElement, setPlayer(x:number,z:number,yaw:number): void, setHidden(h:boolean): void }}
 */
export function createMinimap() {
  const canvas = V(`canvas`, {
    class: `hud-minimap-canvas`,
    "aria-hidden": `true`,
  });
  const el = V(
    `div`,
    {
      class: `hud-minimap`,
      role: `img`,
      "aria-label": `Radar`,
    },
    canvas,
  );

  const ctx = canvas.getContext(`2d`);
  let cssSize = SIZE_DESKTOP;
  let px = At.x,
    pz = At.z,
    yaw = At.yaw;

  function syncSize() {
    cssSize = isMobile() ? SIZE_MOBILE : SIZE_DESKTOP;
    el.style.width = `${cssSize}px`;
    el.style.height = `${cssSize}px`;
    const pxSize = Math.round(cssSize * DPR);
    if (canvas.width !== pxSize) {
      canvas.width = pxSize;
      canvas.height = pxSize;
    }
  }

  function fillRel(x0, z0, x1, z1, fill) {
    const s = SCALE * DPR;
    ctx.fillStyle = fill;
    ctx.fillRect((x0 - px) * s, -(z1 - pz) * s, (x1 - x0) * s, (z1 - z0) * s);
  }

  function strokeRel(x0, z0, x1, z1, color, lw = 1) {
    const s = SCALE * DPR;
    ctx.strokeStyle = color;
    ctx.lineWidth = lw * DPR;
    ctx.strokeRect(
      (x0 - px) * s + 0.5,
      -(z1 - pz) * s + 0.5,
      (x1 - x0) * s - 1,
      (z1 - z0) * s - 1,
    );
  }

  function redraw() {
    syncSize();
    const w = canvas.width,
      h = canvas.height,
      cx = w / 2,
      cy = h / 2,
      r = w / 2 - 1.5 * DPR;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, w, h);

    // Outer ring (CS bezel)
    ctx.beginPath();
    ctx.arc(cx, cy, r + 1.5 * DPR, 0, Math.PI * 2);
    ctx.fillStyle = `#0c1010e6`;
    ctx.fill();
    ctx.lineWidth = 1.5 * DPR;
    ctx.strokeStyle = `#5a7a5a99`;
    ctx.stroke();

    // Clip to radar disc
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.clip();

    // Radar wash
    ctx.fillStyle = `#0a161288`;
    ctx.fillRect(0, 0, w, h);

    // Crosshair grid
    ctx.strokeStyle = `#3d5a4022`;
    ctx.lineWidth = DPR;
    ctx.beginPath();
    ctx.moveTo(cx, cy - r);
    ctx.lineTo(cx, cy + r);
    ctx.moveTo(cx - r, cy);
    ctx.lineTo(cx + r, cy);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(cx, cy, r * 0.45, 0, Math.PI * 2);
    ctx.arc(cx, cy, r * 0.75, 0, Math.PI * 2);
    ctx.strokeStyle = `#3d5a4018`;
    ctx.stroke();

    // World under player (rotate so facing = up)
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(-yaw);

    fillRel(H.xMin, H.zMin, H.xMax, H.zMax, `#1c3320cc`);
    fillRel(
      -U.halfFloor - 0.35,
      U.zStart,
      U.halfFloor + 0.35,
      U.zEnd,
      `#1a2428cc`,
    );
    fillRel(W.xMin, W.zMin, W.xMax, W.zMax, `#281830cc`);
    fillRel(G.xMin, G.zMin, G.xMax, G.zMax, `#3a2850ee`);
    strokeRel(H.xMin, H.zMin, H.xMax, H.zMax, `#4a7a4a88`);
    strokeRel(W.xMin, W.zMin, W.xMax, W.zMax, `#7a4a8a88`);

    // Trail
    if (q.length > 1) {
      const s = SCALE * DPR;
      ctx.beginPath();
      q.forEach(([x, z], i) => {
        const rx = (x - px) * s,
          ry = -(z - pz) * s;
        i ? ctx.lineTo(rx, ry) : ctx.moveTo(rx, ry);
      });
      ctx.strokeStyle = `#c8b070aa`;
      ctx.lineWidth = 2 * DPR;
      ctx.lineJoin = `round`;
      ctx.stroke();
    }

    // Spawn blip
    {
      const s = SCALE * DPR;
      const sx = (At.x - px) * s,
        sy = -(At.z - pz) * s;
      ctx.fillStyle = `#8a9a8a`;
      ctx.fillRect(sx - 1.5 * DPR, sy - 1.5 * DPR, 3 * DPR, 3 * DPR);
    }

    ctx.restore(); // end world rotate
    ctx.restore(); // end clip

    // Player chevron — fixed at center, always faces "up" (CS style)
    const len = Math.max(5, cssSize * 0.09) * DPR;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.beginPath();
    ctx.moveTo(0, -len);
    ctx.lineTo(len * 0.58, len * 0.55);
    ctx.lineTo(0, len * 0.18);
    ctx.lineTo(-len * 0.58, len * 0.55);
    ctx.closePath();
    ctx.fillStyle = `#c8f0c8`;
    ctx.fill();
    ctx.strokeStyle = `#0a120a`;
    ctx.lineWidth = DPR;
    ctx.stroke();
    ctx.restore();

    // Inner rim highlight
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.strokeStyle = `#a8c8a833`;
    ctx.lineWidth = DPR;
    ctx.stroke();
  }

  syncSize();
  redraw();

  const onResize = () => redraw();
  window.addEventListener(`resize`, onResize);
  window.addEventListener(`orientationchange`, onResize);

  return {
    el,
    setPlayer(x, z, facing) {
      px = x;
      pz = z;
      yaw = facing;
      redraw();
    },
    setHidden(hidden) {
      el.classList.toggle(`hidden`, !!hidden);
    },
    dispose() {
      window.removeEventListener(`resize`, onResize);
      window.removeEventListener(`orientationchange`, onResize);
    },
  };
}
